import React from "react"
import { Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer"
import dayjs from "dayjs"
import RobotoRegular from "../../fonts/Roboto/Roboto-Regular.ttf"
import RobotoMedium from "../../fonts/Roboto/Roboto-Medium.ttf"
import RobotoBold from "../../fonts/Roboto/Roboto-Bold.ttf"
import RobotoItalic from "../../fonts/Roboto/Roboto-Italic.ttf"

// Register Roboto family with local font files
Font.register({
  family: "Roboto",
  fonts: [
    { src: RobotoRegular, fontWeight: "normal" },
    { src: RobotoMedium, fontWeight: 500 },
    { src: RobotoBold, fontWeight: 700 },
    { src: RobotoItalic, fontStyle: "italic" },
  ],
})

// Prevent weird word splits/overlaps
Font.registerHyphenationCallback((word) => [word])

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: "Roboto",
  },
  header: {
    fontSize: 16,
    fontWeight: 700,
    textAlign: "center",
    marginBottom: 30,
  },
  section: {
    marginBottom: 15,
  },
  row: {
    flexDirection: "row",
    marginBottom: 8,
  },
  label: {
    width: "40%",
    fontWeight: 700,
  },
  value: {
    width: "60%",
  },
  divider: {
    borderBottom: "1px solid #000",
    marginVertical: 15,
  },
  footer: {
    position: "absolute",
    bottom: 40,
    left: 40,
    right: 40,
    fontSize: 9,
    color: "#666",
  },
})

const TRANSLATIONS = {
  en: {
    title: "REPRINT REQUEST FORM",
    date: "Date:",
    reprintReason: "Reprint Reason:",
    actNumber: "Act No.:",
    datedWith: "Dated:",
    productName: "Product Name:",
    reprintQuantity: "Reprint Quantity:",
    copies: "copies",
    orderNumber: "Order Number:",
    complaintReason: "Complaint Reason:",
    reasons: {
      production: "Production defect",
      layout: "Layout error",
      clientFault: "Defect due to client fault (by agreement)",
      other: "Other",
    },
    deadline: "Deadline:",
    generatedOn: "Generated on:",
  },
  lv: {
    title: "ATKĀRTOTAS DRUKAS PIEPRASĪJUMA FORMA",
    date: "Datums:",
    reprintReason: "Atkārtotas drukas iemesls:",
    actNumber: "Akta Nr.:",
    datedWith: "Datēts ar:",
    productName: "Produkta nosaukums:",
    reprintQuantity: "Atkārtotas drukas daudzums:",
    copies: "eksemplāri",
    orderNumber: "Pasūtījuma numurs:",
    complaintReason: "Atkārtotas drukas (Complaint) iemesls:",
    reasons: {
      production: "Ražošanas defekts",
      layout: "Izkārtojuma kļūda",
      clientFault: "Defekts klienta vainas dēļ (pēc vienošanās)",
      other: "Cits",
    },
    deadline: "Izpildes termiņš:",
    generatedOn: "Ģenerēts:",
  },
}

export default function ReprintPDFDocument({ data, language = "lv" }) {
  const t = TRANSLATIONS[language]

  const formatDate = (date) => {
    if (!date) return "—"
    return dayjs(date).format("DD.MM.YYYY")
  }

  const getReasonText = (reason) => {
    return t.reasons[reason] || reason
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>{t.title}</Text>

        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.label}>{t.actNumber}</Text>
            <Text style={styles.value}>#{data.actNumber}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>{t.date}</Text>
            <Text style={styles.value}>{formatDate(data.date)}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>{t.datedWith}</Text>
            <Text style={styles.value}>{formatDate(data.actDate)}</Text>
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.label}>{t.orderNumber}</Text>
            <Text style={styles.value}>{data.orderNumber}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>{t.productName}</Text>
            <Text style={styles.value}>{data.productName}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>{t.reprintQuantity}</Text>
            <Text style={styles.value}>
              {data.reprintQuantity} {t.copies}
            </Text>
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.label}>{t.complaintReason}</Text>
            <Text style={styles.value}>{getReasonText(data.complaintReason)}</Text>
          </View>

          {data.otherReasonText && (
            <View style={styles.row}>
              <Text style={styles.label}></Text>
              <Text style={styles.value}>{data.otherReasonText}</Text>
            </View>
          )}

          {data.reprintReason && (
            <View style={styles.row}>
              <Text style={styles.label}>{t.reprintReason}</Text>
              <Text style={styles.value}>{data.reprintReason}</Text>
            </View>
          )}
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.label}>{t.deadline}</Text>
            <Text style={styles.value}>{formatDate(data.deadline)}</Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>
            {t.generatedOn} {dayjs().format("DD.MM.YYYY HH:mm")}
          </Text>
        </View>
      </Page>
    </Document>
  )
}
